import { FC, useMemo, useState } from "react";

import { PaymentDetails } from "./PaymentDetails/PaymentDetails";

import { ConnectedChat } from "@/features/Assistant/Chat/ConnectedChat";
import { useAssistantWidget } from "@/features/Assistant/Widget/helpers/useAssistantWidget";
import { AssistantWidgetEvents } from "@/features/Assistant/Widget/helpers/useLogAssistantWidgetEvents";
import { Widget } from "@/features/Assistant/Widget/Widget";
import { PaymentDetailsProvider } from "@/features/DonorAssistant/contexts/PaymentDetailsContext";
import { DONOR_ASSISTANT_CONFIG } from "@/features/DonorAssistant/helpers/donorAssistantConfig";
import { DONOR_CHAT_ADAPTER, DonorAssistantChatControls } from "@/features/DonorAssistant/helpers/donorChatAdapter";
import { AssistantPaymentCard } from "@/features/DonorAssistant/PaymentCards/helpers/parsePaymentCards";
import { DonorPaymentControl } from "@/features/DonorDashboard/DonorPaymentDetailDrawer/types";

interface OpenedPayment {
  payment: AssistantPaymentCard;
  control: DonorPaymentControl | null;
}

export const DonorAssistantWidget: FC<AssistantWidgetEvents> = ({
  onOpen,
  onClose,
  onMessageSent,
  onConversationRestarted,
  onToolRendered,
}) => {
  const [opened, setOpened] = useState<OpenedPayment | null>(null);
  const paymentDetails = useMemo(
    () => ({
      openPayment: (payment: AssistantPaymentCard, control?: DonorPaymentControl) =>
        setOpened({ payment, control: control ?? null }),
    }),
    []
  );
  const widget = useAssistantWidget<DonorAssistantChatControls>({ onOpen, onClose, onConversationRestarted });

  const requestClose = () => {
    setOpened(null);
    widget.requestClose();
  };
  const askAboutOpenedPayment = () => {
    if (opened) {
      widget.controls?.askAboutPayment(opened.payment);
    }
    setOpened(null);
  };

  return (
    <Widget
      feature={DONOR_ASSISTANT_CONFIG.feature}
      widget={{ ...widget, requestClose }}
      details={
        opened && (
          <PaymentDetails
            payment={opened.payment}
            initialControl={opened.control}
            onBack={() => setOpened(null)}
            onClose={requestClose}
            onAskAbout={askAboutOpenedPayment}
          />
        )
      }
      onCloseDetails={() => setOpened(null)}
    >
      <PaymentDetailsProvider value={paymentDetails}>
        <ConnectedChat
          adapter={DONOR_CHAT_ADAPTER}
          onControlsChange={widget.setControls}
          onMessageSent={onMessageSent}
          onToolRendered={onToolRendered}
        />
      </PaymentDetailsProvider>
    </Widget>
  );
};
